import { LanguageTag } from '@flipster/i18n/types';

import { apiClient } from '../../axios';
import { ReqPagination } from '../../types';
import {
  Auth,
  DeleteUserPasskeyCredentialInterimResponse,
  DeleteUserPasskeyCredentialParams,
  DeleteUserPasskeyCredentialResponse,
  DeleteUserTotpDeleteParams,
  DeleteUserTradeDefaultsParams,
  GeetestCaptchaResponse,
  GetAbTestResponse,
  GetFeatureHighlightsResponse,
  GetFeatureUpdatesResponse,
  GetPromoSubsResponse,
  GetTradingActivityResponse,
  GetTranslatedProfileResponse,
  GetUnmaskedInfoParams,
  GetUsd1PointHistoryParams,
  GetUsd1PointHistoryResponse,
  GetUsd1PointResponse,
  GetUserBlockParams,
  GetUserBlockResponse,
  GetUserDevicesResponse,
  GetUserFeedsResponse,
  GetUserFollowersResponse,
  GetUserFollowingResponse,
  GetUserLossProtectionResponse,
  GetUserPasskeyCredentialsResponse,
  GetUserPointHistoryResponse,
  GetUserPointResponse,
  GetUserProfileByProfileIdResponse,
  GetUserProfileResponse,
  GetUserSecurityAuthModeResponse,
  GetUserTierResponse,
  GetUserVerificationResponse,
  NotificationGroup,
  NotificationsResponse,
  PostPromoSubsParams,
  PostUserAuthMfaConfirmParams,
  PostUserAuthMfaConfirmResponse,
  PostUserChangePasswordParams,
  PostUserLoginParams,
  PostUserLoginResponse,
  PostUserPasskeyAuthenticationOptionsResponse,
  PostUserPasskeyAuthenticationParams,
  PostUserPasskeyAuthenticationResponse,
  PostUserPasskeyRegistrationOptionsParams,
  PostUserPasskeyRegistrationOptionsResponse,
  PostUserPasskeyRegistrationParams,
  PostUserPhoneChangeParams,
  PostUserProfileImageParams,
  PostUserProfileImageResponse,
  PostUserProfileSocialConnectXParams,
  PostUserProfileSocialConnectXResponse,
  PostUserResetPasswordParams,
  PostUserSignupParams,
  PostUserSignupResponse,
  PostUserSmsRequestResponse,
  PostUserTotpConfirmParams,
  PostUserTotpRequestResponse,
  PostUserVerificationParams,
  PutUserChangePasswordParams,
  PutUserNicknameParams,
  PutUserProfileNotificationParams,
  PutUserProfileParams,
  PutUserSecurityAuthModeParams,
  PutUserTradeDefaultsParams,
  RestrictedUserRegion,
  TradingActivityPositionStatus,
  UnmaskedInfo,
  UpdateNotificationGroupReq,
  UserReferralInfo,
} from './types';

export const getMeApi = () => apiClient.get<Auth>('/v1/user/me');

export const putUserNicknameApi = (data: PutUserNicknameParams) =>
  apiClient.put<PutUserNicknameParams>('/v1/user/me', data);

export const getUserRegionApi = () => apiClient.get<RestrictedUserRegion>('/v1/user/region');
export const getUserReferralInfoApi = () => apiClient.get<UserReferralInfo>('/v1/user/referral');

export const getUserBlockApi = (params: GetUserBlockParams) =>
  apiClient.get<GetUserBlockResponse>('/v1/user/block', { params });

export const postUserLangApi = (lang: LanguageTag) => apiClient.post('/v1/user/lang', { lang });

// session
export const postUserLoginApi = (params: PostUserLoginParams) =>
  apiClient.post<PostUserLoginResponse>('/v1/user/auth', params);

export const getUserSecurityAuthModeApi = () =>
  apiClient.get<GetUserSecurityAuthModeResponse>('/v1/user/security/auth-mode');

export const putUserSecurityAuthModeApi = (params: PutUserSecurityAuthModeParams) =>
  apiClient.put('/v1/user/security/auth-mode', params);

export const postUserAuthMfaConfirmApi = (params: PostUserAuthMfaConfirmParams) =>
  apiClient.post<PostUserAuthMfaConfirmResponse>('/v1/user/auth/mfa-confirm', params);

export const postUserSignupApi = (params: PostUserSignupParams) =>
  apiClient.post<PostUserSignupResponse>('/v1/user/create', params);

export const postUserLogOutApi = (nuts: string) =>
  apiClient.post('/v1/user/logout', nuts, {
    headers: {
      'Content-Type': 'text/plain',
    },
  });

export const postUserEmailPasswordApi = (params: { email: string; geetestCaptchaResponse?: GeetestCaptchaResponse }) =>
  apiClient.post('/v1/user/email/password', params);

export const putUserPasswordApi = (data: PutUserChangePasswordParams) => apiClient.put('/v1/user/password', data);

export const postUserPasswordApi = (data: PostUserChangePasswordParams) => apiClient.post('/v1/user/password', data);

export const getUserResetPasswordApi = (params: { email: string; resetToken: string }) =>
  apiClient.get('/v1/user/reset-password', { params });

export const postUserVerifyApi = (params: { email: string; hash: string }) => apiClient.post('/v1/user/verify', params);

export const postUserResetPasswordApi = (params: PostUserResetPasswordParams) =>
  apiClient.post('/v1/user/reset-password', params);

export const getUserUnmaskedInfo = (params: GetUnmaskedInfoParams) =>
  apiClient.get<UnmaskedInfo>('/v1/user/unmasked-info', { params });

// 2fa
export const postUserTotpRequestApi = () => apiClient.post<PostUserTotpRequestResponse>('/v1/user/totp/request');

export const postUserTotpConfirmApi = (params: PostUserTotpConfirmParams) =>
  apiClient.post('/v1/user/totp/mfa-confirm', params);

export const deleteUserTotpDeleteApi = (data: DeleteUserTotpDeleteParams) =>
  apiClient.delete('/v1/user/totp/delete', { data });

// phone number
export const postUserSmsRequestApi = (params: { phoneNumber: string }) =>
  apiClient.post<PostUserSmsRequestResponse>('/v1/sms/request', params);

export const postUserSmsVerifyApi = (params: { sessionToken: string; smsToken: string }) =>
  apiClient.post('/v1/sms/verify', params);

export const postUserPhoneChangeApi = (params: PostUserPhoneChangeParams) =>
  apiClient.post('/v1/user/phone/change', params);

export const getUserTierApi = () => apiClient.get<GetUserTierResponse>('/v1/user/tier');

// passkey
export const postUserPasskeyAuthenticationOptionsApi = () =>
  apiClient.post<PostUserPasskeyAuthenticationOptionsResponse>('/v1/user/passkey/authentication/options');

export const postUserPasskeyAuthenticationApi = (params: PostUserPasskeyAuthenticationParams) =>
  apiClient.post<PostUserPasskeyAuthenticationResponse>('/v1/user/passkey/authentication', params);

export const deleteUserPasskeyCredentialInterimApi = (credentialId: string) =>
  apiClient.delete<DeleteUserPasskeyCredentialInterimResponse>(`/v1/user/passkey/credential/${credentialId}/interim`);

export const deleteUserPasskeyCredentialApi = (credentialId: string, data: DeleteUserPasskeyCredentialParams) =>
  apiClient.delete<DeleteUserPasskeyCredentialResponse>(`/v1/user/passkey/credential/${credentialId}`, { data });

export const getUserPasskeyCredentialsApi = () =>
  apiClient.get<GetUserPasskeyCredentialsResponse>('/v1/user/passkey/credentials');

export const postUserPasskeyRegistrationOptionsApi = (params: PostUserPasskeyRegistrationOptionsParams) =>
  apiClient.post<PostUserPasskeyRegistrationOptionsResponse>('/v1/user/passkey/registration/options', params);

export const postUserPasskeyRegistrationApi = (params: PostUserPasskeyRegistrationParams) =>
  apiClient.post('/v1/user/passkey/registration', params);

// user point
export const getUserPointApi = () => apiClient.get<GetUserPointResponse>('/v1/user/point');

export const getUserPointHistoryApi = (params?: {
  before?: number;
  after?: number;
  startTime?: string;
  endTime?: string;
  count?: number;
}) => apiClient.get<GetUserPointHistoryResponse>('/v1/user/point/history', { params });

export const getUserLossProtectionApi = () => apiClient.get<GetUserLossProtectionResponse>('/v1/user/loss-protection');

// profile
export const getUserProfileApi = () => apiClient.get<GetUserProfileResponse>('/v1/user/profile');

export const getUserProfileByProfileIdApi = (profileId: string) =>
  apiClient.get<GetUserProfileByProfileIdResponse>(`/v1/user/profile/${profileId}`);

export const putUserProfileApi = (data: PutUserProfileParams) => apiClient.put('/v1/user/profile', data);

export const postUserProfileImageApi = (data: PostUserProfileImageParams) =>
  apiClient.post<PostUserProfileImageResponse>('/v1/user/profile/profile-image', data);

export const putUserProfileNotificationApi = (profileId: string, data: PutUserProfileNotificationParams) =>
  apiClient.put(`/v1/user/profile/${profileId}/notifications`, data);

export const getTradingActivityApi = (
  profileId: string,
  params: { positionStatus: TradingActivityPositionStatus } & ReqPagination,
) => apiClient.get<GetTradingActivityResponse>(`/v1/user/profile/${profileId}/trading-activity`, { params });

export const getUserFeedsApi = (profileId: string, params: Exclude<ReqPagination, 'before'>) =>
  apiClient.get<GetUserFeedsResponse>(`/v1/user/profile/${profileId}/feeds`, { params });

// profile - oauth
export const postUserProfileSocialConnectXApi = (data: PostUserProfileSocialConnectXParams) =>
  apiClient.post<PostUserProfileSocialConnectXResponse>(`/v1/user/profile/social-connect/x`, data);

export const postUserProfileSocialDisconnectXApi = () => apiClient.post(`v1/user/profile/social-disconnect/x`);

// follow
export const getUserFollowersApi = (profileId: string, params: ReqPagination) =>
  apiClient.get<GetUserFollowersResponse>(`/v1/user/profile/${profileId}/followers`, { params });

export const getUserFollowingApi = (profileId: string, params: ReqPagination) =>
  apiClient.get<GetUserFollowingResponse>(`/v1/user/profile/${profileId}/following`, { params });

export const postUserFollowApi = (profileId: string) => apiClient.post(`/v1/user/profile/${profileId}/follow`);

export const deleteUserFollowApi = (profileId: string) => apiClient.delete(`/v1/user/profile/${profileId}/follow`);

// ab test
export const getAbTestApi = () => apiClient.get<GetAbTestResponse>('/v1/user/ab-test');

// TODO: [sh] security 쪽으로 옮기기
// user devices
export const getUserDevicesApi = (params: ReqPagination) =>
  apiClient.get<GetUserDevicesResponse>('/v1/user/devices', { params });
export const postUserDeviceAllLogoutApi = () => apiClient.post('/v1/user/logout/all');
export const postUserDeviceLogoutApi = (deviceId: string) => apiClient.post(`/v1/user/logout/device/${deviceId}`);

// marketing
export const getPromotionSubscriptionsApi = () =>
  apiClient.get<GetPromoSubsResponse>('/v1/user/promotional-subscriptions');
export const postPromotionSubscriptionApi = (params: PostPromoSubsParams) =>
  apiClient.post('/v1/user/promotional-subscriptions', params);
export const getPromotionSubscriptionPopupApi = () =>
  apiClient.get<{ showPopup: boolean }>('/v1/user/promotional-subscription-popup');
export const postPromotionSubscriptionPopupDismissApi = () =>
  apiClient.post('/v1/user/promotional-subscription-popup/dismiss');

export const getUserNotifications = (params?: { cursor?: string; direction?: 'desc' | 'asc'; count?: number }) =>
  apiClient.get<NotificationsResponse>('/v1/user/notifications', { params });

export const markAllNotificationsAsRead = (cursor: string) =>
  apiClient.put('/v1/user/notifications/mark-all-as-read', undefined, { params: { cursor } });

export const markNotificationAsRead = (notificationId: string) =>
  apiClient.put(`/v1/user/notifications/${notificationId}/mark-as-read`);

export const getNotificationGroups = () =>
  apiClient.get<{ notificationGroups: NotificationGroup[] }>('/v1/user/notifications/groups');

export const updateNotificationGroup = (groupCode: string, body: UpdateNotificationGroupReq) =>
  apiClient.put(`/v1/user/notifications/groups/${groupCode}`, body);

// usd1
export const getUsd1PointApi = () => apiClient.get<GetUsd1PointResponse>('/v1/user/usd1/point');

export const getUsd1PointHistoryApi = (params?: GetUsd1PointHistoryParams) =>
  apiClient.get<GetUsd1PointHistoryResponse>('/v1/user/usd1/point/history', { params });

// feature updates
export const getFeatureUpdates = () => apiClient.get<GetFeatureUpdatesResponse>('/v1/feature/updates');

export const getFeatureHighlights = () => apiClient.get<GetFeatureHighlightsResponse>('/v1/feature/highlights');

// user verification
export const getUserVerificationApi = (requestId: string) =>
  apiClient.get<GetUserVerificationResponse>('/v1/user/security/auth/info', { params: { requestId } });

export const postUserVerificationApi = (body: PostUserVerificationParams) =>
  apiClient.post('/v1/user/security/auth', body);

export const getTranslatedProfileApi = (profileId: string, params: { lang: string }) =>
  apiClient.get<GetTranslatedProfileResponse>(`/v1/user/profile/${profileId}/translated`, { params });

// trade defaults
export const putUserTradeDefaultsApi = (params: PutUserTradeDefaultsParams) =>
  apiClient.put('/v1/user/trade-defaults', params);

export const deleteUserTradeDefaultsApi = (data: DeleteUserTradeDefaultsParams) =>
  apiClient.delete('/v1/user/trade-defaults', { data });
